import classNames from "classnames";
import { FC, SyntheticEvent, useEffect, useRef, useState } from "react";
import EmbedConsent from "rivals/components/shared/EmbedConsent";
import { FEATURED_MEDIA_ROOT_MARGIN } from "rivals/components/shared/VideoPlayer";
import styles from "rivals/components/shared/VideoPlayer/VideoPlayer.module.scss";
import { ROOT_SITE_IDENTIFIER } from "rivals/shared/constants";
import { useIsMobileWidth } from "rivals/shared/utils/mediaQuery";
import { setData } from "rivals/shared/utils/qa";
import { useElementOnScreen } from "rivals/shared/utils/useElementOnScreen";
import { Props } from "./types";

const BrightcovePlayer: FC<Props> = ({
  autoplay,
  blockId,
  className,
  contentSpaceId,
  currentVideoBlockId,
  hasEmbedConsent,
  mute,
  observerOptions,
  onVisibilityChange,
  philter,
  playOnHover,
  promptEmbedConsent = true,
  setVideosOnScreen = (): void => {},
  srcId,
  userCountry,
  videosOnScreen = [],
  ...props
}) => {
  const isMobile = useIsMobileWidth();
  const brightcoveRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<YAHOO.VideoPlatform.VideoPlayer | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  // Determine video visibility for featured media page mobile view
  const { isVisible } = useElementOnScreen({
    options: {
      root: null,
      rootMargin: FEATURED_MEDIA_ROOT_MARGIN,
      threshold: 0,
      ...observerOptions
    },
    target: isMobile ? brightcoveRef.current : null
  });

  // Determine video visibility of Brightcove videos for article page
  const { isVisible: onScreen } = useElementOnScreen({
    options: {
      root: null,
      rootMargin: "-30% 0px -30% 0px",
      threshold: 0
    },
    target: brightcoveRef.current
  });

  const onMouseOver = (event: SyntheticEvent<HTMLDivElement>): void => {
    if (props?.onMouseOver) {
      props.onMouseOver(event);
    }
    if (playOnHover && playerRef?.current) {
      playerRef.current.play();
    }
  };

  const onMouseOut = (event: SyntheticEvent<HTMLDivElement>): void => {
    if (props?.onMouseOut) {
      props?.onMouseOut(event);
    }
    if (playOnHover && playerRef?.current) {
      playerRef.current.pause();
    }
  };

  /**
   *   Render video once all necessary data is present
   *   The player config is static and parameters do not change during life cycle of the player so in order to apply new changes, the player must be re-initialized.
   */
  useEffect(() => {
    if (hasEmbedConsent) {
      setIsLoaded(false);
      let config;
      // TODO: switch from NEXT_PUBLIC_COMSCORE_ENABLED ENV to flipper
      if (process.env.NEXT_PUBLIC_COMSCORE_ENABLED === "true") {
        config = {
          // default autoplay === undefined to true
          autoplay: autoplay !== false,
          comscoreC4: "rivals",
          hidePlaylist: true,
          items: [
            {
              id: srcId,
              mimetype: "media/sapi"
            }
          ],
          lang: "en-US",
          // if autoplay, always mute
          mute: mute ?? autoplay !== false,
          pageSpaceId: contentSpaceId,
          recommendation: false,
          region: userCountry,
          site: ROOT_SITE_IDENTIFIER
        };
      } else {
        config = {
          // default autoplay === undefined to true
          autoplay: autoplay !== false,
          hidePlaylist: true,
          items: [
            {
              id: srcId,
              mimetype: "media/sapi"
            }
          ],
          lang: "en-US",
          // if autoplay, always mute
          mute: mute ?? autoplay !== false,
          pageSpaceId: contentSpaceId,
          recommendation: false,
          region: userCountry,
          site: ROOT_SITE_IDENTIFIER
        };
      }

      if (brightcoveRef?.current) {
        /**
         *  A race condition exist when multiple props can change while the player is initializing but the player has not rendered yet resulting in duplicate videos.
         *  This will remove the children of the container to prevent multiple renders of the same video.
         */
        brightcoveRef?.current.replaceChildren();
      }
      playerRef.current = new YAHOO.VideoPlatform.VideoPlayer(config);
      playerRef.current.render(`#brightcove-container-${blockId}`);
      playerRef.current.on(
        // When video fully loads
        YAHOO.VideoPlatform.Events.PLAYER_READY,
        function () {
          // Give the other useEffect data to re-render off of
          setIsLoaded(true);
        }
      );
    }
  }, [
    autoplay,
    blockId,
    contentSpaceId,
    hasEmbedConsent,
    mute,
    srcId,
    userCountry
  ]);

  // Handle video autoplay on featured media page
  useEffect(() => {
    if (onVisibilityChange) {
      onVisibilityChange(isVisible);
      // Brightcove autoplay
      if (isLoaded && isVisible && isMobile) {
        playerRef?.current?.play();
      } else if (isLoaded && !isVisible && isMobile) {
        playerRef?.current?.pause();
      }
    }
  }, [isLoaded, isVisible, onVisibilityChange, isMobile]);

  // Set Brightcove videos on screen for article page
  useEffect(() => {
    if (isLoaded && onScreen && blockId) {
      setVideosOnScreen([...videosOnScreen, blockId]);
    } else if (isLoaded && !onScreen && blockId) {
      setVideosOnScreen(videosOnScreen.filter(item => item !== blockId));
    }
    // Should not use the disable but need to set videos on screen without it being a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onScreen, isLoaded, setVideosOnScreen, blockId]);

  // Handle Brightcove autoplay for article page
  useEffect(() => {
    if (currentVideoBlockId === blockId) {
      playerRef?.current?.play();
    } else if (currentVideoBlockId !== blockId) {
      playerRef?.current?.pause();
    }
  }, [currentVideoBlockId, blockId]);

  if (!hasEmbedConsent) {
    if (promptEmbedConsent) {
      return <EmbedConsent />;
    } else {
      return null;
    }
  }

  if (!srcId) {
    return null;
  }

  return (
    <div
      className={classNames(styles.videoPlayer, className)}
      onBlur={onMouseOut}
      onFocus={onMouseOver}
      onMouseOut={onMouseOut}
      onMouseOver={onMouseOver}
      role="button"
      {...setData(philter, "container")}
      tabIndex={0}
    >
      {contentSpaceId ? (
        <div
          id={`brightcove-container-${blockId}`}
          ref={brightcoveRef}
          style={{ height: "100%", width: "100%" }}
          {...setData(philter, "brightcove")}
        />
      ) : null}
    </div>
  );
};

export default BrightcovePlayer;
